import { animate, state, style, transition, trigger } from '@angular/animations';
import {
    AfterViewInit,
    Component, ContentChild, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef,
    ViewChild
} from '@angular/core';
import {LazyLoadEvent, SortEvent} from 'primeng/api';
import {Table} from 'primeng/table';

export interface Column {
    field: string;
    header: string;
    customGetter?: Function;
    sortable?: boolean;
    sortField?: string;
    columnType?: string;
    isNotes?: boolean;
    translateHeader?: boolean;
}

export class SearchResultColumn implements Column {
    field: string;
    header: string;
    sortable: boolean;
    sortField: string;
    translateHeader = false;

    constructor(field: string, header: string, sortable: boolean, sortField: string, translateHeader?: boolean) {
        this.field = field;
        this.header = header;
        this.sortable = sortable;
        this.sortField = sortField;
        this.translateHeader = translateHeader;
    }

}

export class Action {
    value: string;
    label: string;
    facetName: string;
    columns: Column[];

    constructor(value: string, label: string, facetName: string, columns: Column[]) {
        this.value = value;
        this.label = label;
        this.facetName = facetName;
        this.columns = columns;
    }

}

export interface TableChangeEvent {
    eventType: string;
    param: any;
}

export interface TableConfig {
    rows?: number;

    pagination ?: {
        rows: number;
    };

    isStatic ?: boolean;

    noPagination ?: boolean;

    paginatorPosition?: string;

    scrollable ?: boolean;

    scrollHeight ?: string;

    virtualScroll?: boolean;

    filterOn?: string[];

    showInfo?: boolean;

    globalFilters?: string[];

    searchOnList?: string;

    showGoto?: boolean;

    sortField?: string;

    multiSortMeta?: any[];

    sortOrder?: number;

    startRow?: number;

    filterValue?: string;

    sortMode?: string;

    hideSearch?: boolean;

    hideRowCount?: boolean;

    selectionMode?: string;

    selectedRow?: any;

    defaultSortMeta?: FieldSortInfo[];

    minLengthForFilter?: number;
    
    columnResizeMode?:string;
    isresponsive? : boolean;
    rowexpandable? : boolean; 
    dataKey? : string;
}

export interface FieldSortInfo {
    field: string;
    order: number;
}

export interface DefaultMetaInfo {
    sortInfo?: FieldSortInfo[];
}

const ERROR_MSGS = {
    PAGE_NO_INVALID: 'Invalid Goto Page Number'
};

@Component({
    selector: 'copart-table',
    templateUrl: './table.component.html',
    styleUrls: ['./table.component.scss'],
    animations: [
        trigger('rowExpansionTrigger', [
            state('void', style({
                transform: 'translateX(-10%)',
                opacity: 0
            })),
            state('active', style({
                transform: 'translateX(0)',
                opacity: 1
            })),
            transition('* <=> *', animate('400ms cubic-bezier(0.86, 0, 0.07, 1)'))
        ])
    ]
})
export class TableComponent implements OnInit, AfterViewInit {


    @Input() translateObject: any;
  /**
     * Data to be displayed in the table
     */
    @Input() data;

    /**
     * This function is used to show loading icon, status suggests if the data is loaded or not
     * {boolean} : data loaded or not
     */
    @Input() loading: boolean;

    /**
     * Total number of records available in the server
     */
    @Input() totalRecords;

    /**
     * Columns that should be displayed
     */
    @Input() columns: [Column];
    /**
     * Additional settings that needs to be applied
     *
     */
    @Input() config: TableConfig = {};

    /**
     * Styles that should be applied
     */
    @Input() style;

    /**
     * Event emitted to fetch/apply new filters and search criteria.
     *  {EventEmitter<any>}
     */
    @Output() updateData: EventEmitter<any>;

    /**
     * Emits a change event whenever filter,sort or pagination is applied on table
     */
    @Output() onChange: EventEmitter<any>;

    /**
     * Reference to custom cell templates.
     */
    @ContentChild(TemplateRef,  {static: true}) tableBodyTemplate;
    @ContentChild('expanded',  {static: false}) tableExpandedBodyTemplate;


    /**
     * Current page number
     */
    pageNumber: number;

    /**
     * Stores the reference of the table
     */
    @ViewChild('mt', { static: true }) table: Table;

    /**
     * Stores the reference of the search bar
     */
    @ViewChild('searchBar', {static: false}) searchBar: ElementRef;

    /**
     * Used to save search text and restore context
     */
    //   globalFilter: string;

    /**
     * Used to show error related to go to button
     */
    public msgs: any;

    /**
     * Used to updateRow Selection
     */
    @Output() onSelectionChange: EventEmitter<any>;

    @Output() onRowClick: EventEmitter<any>;

    /**
     * Used to reset page to first page, when filters are applied
     *  {boolean} reset
     */

    sorted: boolean;

    @Input() defaultMetaInfo: DefaultMetaInfo;

    @Input('resetPage') set page(reset: boolean) {
        if (reset) {
            this.table.first = 0;
            this.pageNumber = null;
        }
    }

    constructor() {
        this.loading = false;
        this.config = {startRow: 0 , dataKey : '' , isresponsive : true};
        this.msgs = [];
        this.onChange = new EventEmitter<TableChangeEvent>();
        this.updateData = new EventEmitter<any>();
        this.onSelectionChange = new EventEmitter<any>();
        this.onRowClick = new EventEmitter<any>();
    }

    ngOnInit() {
        this.sorted = true;
        if (this.config.filterValue) {
            this.sorted = false;
        }
       if (typeof(this.config.isresponsive) == "undefined" || this.config.isresponsive == null) {
            this.config.isresponsive = true;
        }

    }

    ngAfterViewInit() {
        if (this.config.filterValue) {
            this.searchBar.nativeElement.value = this.config.filterValue;
        }
    }

    /**
     * Gets the data from a rest service
     *  {LazyLoadEvent} event : PrimeNg table event object.
     */
    getData(event: LazyLoadEvent) {
        if (!event.filters.global && !this.config.filterValue) {
            if (event.multiSortMeta) {
                this.config.multiSortMeta = event.multiSortMeta;
            } else {
                this.config.multiSortMeta = this.defaultMetaInfo && this.defaultMetaInfo.sortInfo;
             }
        } else {
               this.config.multiSortMeta = null;
         }
        if (this.config.filterValue) {
            event.globalFilter = this.config.filterValue;
        }
        this.updateData.emit(event);
    }


    /**
     * This function provides the data to be displayed in the table.
     *  {Column} col : column who's data needs to me displayed.
     *  item : row data.
     *  {string} : returns the value to be displayed.
     */
    getValue(col: Column, item: any): string {
      if (col.customGetter) {
        return col.customGetter(item[col.field]);
      }
      return item[col.field];
    }

    gotoPage() {
        if (this.pageNumber && this.pageNumber !== Math.ceil(this.table.first / this.table.rows) + 1) {
            this.table.first = (this.pageNumber - 1) * this.table.rows > this.table.totalRecords ? 0 :
                (this.pageNumber - 1) * this.table.rows;
            this.pageNumber = null;
            this.table.onPageChange({
                first: this.table.first,
                rows: this.table.rows
            });
        } else {
            this.showErrorMsg(ERROR_MSGS.PAGE_NO_INVALID);
        }
    }

    /**
     * This function is used to get the row number of the last row that is being displayed by the table
     *  {number} : row number of the last row
     */
    getPageLastRow() {
        const total = this.table.first + this.table.rows;
        return total > this.table.totalRecords ? this.table.totalRecords : total;
    }

    getPageCount() {
        return Math.ceil(this.table.totalRecords / this.table.rows);
    }

    /**
     * Shows error message
     */
    showErrorMsg(msg: string) {
        this.msgs = [];
        this.msgs.push({severity: 'error', summary: msg, detail: ''});
    }

    filter(filterText: string) {
        if(this.config.minLengthForFilter && filterText.length>0 && filterText.length < this.config.minLengthForFilter){
            return;
        }
        if (this.sorted && !this.config.isStatic) {
            this.sorted = false;
            this.table.reset();
        }
        this.table._multiSortMeta = this.config.multiSortMeta;
        this.config.filterValue = filterText;
        this.table.filterGlobal(filterText, 'contains');
     }

    emitChange(eventType, event) {
        if (eventType === 'sort') {
            this.sorted = true;
        }
        this.onChange.emit({eventType, param : event});
    }

    onRowSelect(event) {
        this.onSelectionChange.emit(event.data);
        this.onRowClick.emit(event.data);
    }

    onRowUnselect(event) {
        this.onSelectionChange.emit(null);
        this.onRowClick.emit(event.data);
    }

}

